import React, {useState, useEffect, useContext} from 'react';
import axios from "../common/axios";
import useDrivePicker from 'react-google-drive-picker';
import {Form, Input, Select, Divider, Checkbox, Button, Table, Tooltip, Space, Upload} from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import {ContentContext} from "../common/ContentContext";
import {UserContext} from "../common/UserContext";
import useInterval from "../hooks/useInterval";
import CircularLoader from "../common/CircularLoader";

const ContentImport = () => {
    const {content} = useContext(ContentContext);
    const {user} = useContext(UserContext);
    const [contentImportForm] = Form.useForm();
    const [driveFile, setDriveFile] = useState();
    const [syllabusGroupId, setSyllabusGroupId] = useState();
    const [syllabusId, setSyllabusId] = useState();
    const [message, setMessage] = useState('');
    const [contentImport, setContentImport] = useState();
    const [contentImports, setContentImports] = useState([]);
    const [openPicker, authResponse] = useDrivePicker();
    const [token, setToken] = useState();
    const [isLoading, setLoading] = useState(false);

    const validateContent = () => {
        if(!driveFile || !driveFile.id || !syllabusId) {
            return;
        }
        setLoading(true);
        axios.post('/web/ui/content/import/validateContent', {}, {params: {contentFileId: driveFile.id, syllabusId: syllabusId}})
            .then(response => {
                setContentImport(response.data);
                findContentImports();
                setLoading(false);
            });
    }

    const handleOpenPicker = () => {
        let  accessToken =  undefined;
        if(token) {
            if((new Date().getTime()) < token.expiryTime) {
                accessToken = token.accessToken;
            }
        }

        openPicker({
            clientId: "870966665234-gen30igve29sabqlinp3jf08q32gpd5k.apps.googleusercontent.com",
            developerKey: "AIzaSyCFZgE5yZ5-hgTQ-DgRxO2OOzGGSgJ3Jw8",
            viewMimeTypes: "application/vnd.google-apps.folder,application/zip,application/x-zip-compressed",
            showUploadView: false,
            showUploadFolders: false,
            supportDrives: true,
            multiselect: false,
            token: accessToken,
            callbackFunction: (data) => {
                if(data.action === 'picked') {
                    if(data.docs && data.docs.length > 0) {
                        const file = data.docs[0];
                        setDriveFile(file);
                    }
                }
            }
        })
    }

    const handleValidateContent = () => {
        if(!driveFile) {
            return;
        }
        validateContent();
    }

    const handleSyllabusGroupChange = (syllabusGroupId) => {
        setSyllabusGroupId(syllabusGroupId);
    }

    const handleSyllabusChange = (syllabusId) => {
        setSyllabusId(syllabusId);
    }

    const onFinish = (values) => {
        console.log('Success:', values);
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
    };

    const findContentImports = () => {
        axios.get('/web/ui/content/import/findContentImports').then((response) => {
            setContentImports(response.data);
        }).catch(function (e) {
            console.error('Error fetching imports', error);
            setMessage('Error fetching imports');
        });
    };

    const reloadInProgressContentImports = () => {
        if(contentImports && contentImports.some(contentImport => contentImport.inProgress === true)) {
            findContentImports();
        }
    };

    const handleValidationFileDownload = async (contentImportId, validationFileName) => {
        if(!contentImportId || !validationFileName) {
            return
        }

        try {
            const response = await axios.get('/web/ui/content/import/downloadContentValidation', {params: {contentImportId: contentImportId}}, {
                responseType: 'blob', // Response type as blob for binary data
            });

            // Create a blob URL for the file
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);

            // Create an anchor element to trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = validationFileName;
            a.click();

            // Release the object URL to avoid memory leaks
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        findContentImports();
        setSyllabusGroupId(content.currentSyllabus ? content.currentSyllabus.syllabusGroupId : user.syllabusGroupId);
        setSyllabusId(content.currentSyllabus ? content.currentSyllabus.syllabusId : user.syllabusId);
        const contentImportToken = sessionStorage.getItem('contentImportToken');
        if(contentImportToken) {
            setToken(JSON.parse(contentImportToken));
        }
    }, []);


    useEffect(() => {
        if(authResponse) {
            const accessToken = authResponse['access_token'];
            const expiryTime = (authResponse['expires_in'] * 1000 ) + new Date().getTime();

            const contentImportToken  = {accessToken: accessToken, expiryTime: expiryTime};
            sessionStorage.setItem('contentImportToken', JSON.stringify(contentImportToken));
            setToken(contentImportToken);

        }
    }, [authResponse]);

    useInterval(reloadInProgressContentImports, 5000);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'createDate',
            key: 'createDate',
            render: createDate => user.dateTimeFormat.format(createDate)
        },
        {
            title: 'User',
            dataIndex: 'updateUserFullName',
            key: 'updateUserFullName',
        },
        {
            title: 'File name',
            dataIndex: 'contentFileName',
            key: 'contentFileName',
        },
        {
            title: 'Course',
            dataIndex: 'syllabusName',
            key: 'syllabusName',
        },
        {
            title: 'File size',
            dataIndex: 'contentFileSize',
            key: 'contentFileSize',
        },
        {
            title: 'File Count',
            dataIndex: 'fileCount',
            key: 'fileCount',
        },
        {
            title: 'Error Count',
            dataIndex: 'validationErrorCount',
            key: 'validationErrorCount',
        },
        {
            title: 'Status',
            dataIndex: 'importStatusDescription',
            key: 'importStatusDescription',
        },
        {
            title: 'Validation',
            key: 'validationReport',
            render: (_, locContentImport) => {
                if(locContentImport.importStatus ==  'VALIDATED') {
                   return <Space size="middle">
                        <Button type="primary" icon={<DownloadOutlined />} onClick={() => handleValidationFileDownload(locContentImport.contentImportId, locContentImport.validationFileName)}>
                            Download
                        </Button>
                    </Space>
                } else if(locContentImport.inProgress) {
                    return <CircularLoader pageCentered={false}/>;
                }

                return null;
            },
        }
    ];

    return (
        <div>
            <h2>Import Content</h2>
            <Form
                name="contentImportForm"
                form={contentImportForm}
                autoComplete="off"
                layout="horizontal"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
            >
                <Form.Item label="Series" style={{marginBottom: '8px'}} required>
                    <Select
                        style={{ width: 600 }}
                        value={syllabusGroupId}
                        onChange={handleSyllabusGroupChange}
                        options={content.syllabusGroupList.map(syllabusGroup => ({value: syllabusGroup.syllabusGroupId, label: syllabusGroup.syllabusGroupName}))}
                    />
                </Form.Item>
                <Form.Item label="Course" style={{marginBottom: '8px'}} required>
                    <Select
                        style={{ width: 600 }}
                        value={syllabusId}
                        onChange={handleSyllabusChange}
                        options={content.syllabusList.filter(syllabus => syllabus.syllabusGroupId == syllabusGroupId).map(syllabus => ({value: syllabus.syllabusId, label: syllabus.syllabusName}))}
                    />
                </Form.Item>

                <Form.Item label="Select content ZIP file" style={{marginBottom: '8px'}} required>
                    <Button icon={<UploadOutlined />} onClick={handleOpenPicker}>Select File</Button>
                </Form.Item>

                {driveFile && (
                    <Form.Item label="Validate content file">
                        <Button type="primary" onClick={handleValidateContent}>Validate</Button>
                        <span style={{marginLeft: '8px'}}>{driveFile.name}</span>
                        <CircularLoader pageCentered={false} displayInElement={true} visible={isLoading}/>
                    </Form.Item>
                )}
            </Form>

            <div>{message}</div>

            {contentImports != undefined && contentImports.length > 0 &&
                <div>
                    <h2>Recent Imports</h2>
                    <Table dataSource={contentImports} columns={columns} rowKey="contentImportId"/>
                </div>
            }
        </div>
    );
};

export default ContentImport;
